import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  getExpenseProjects,
  createExpenseProject,
  updateExpenseProject,
  deleteExpenseProject,
  batchDeleteExpenseProjects,
} from '../../../services/expenseProjectService';

// Masraf projeleri — liste + form. Tutarlar masraf panosunun ₺ tabanıyla aynıdır (onaylı,
// ₺, yalnız masraf tipi); bütçe kullanımı onaylı harcama / bütçe.

export const PROJECT_STATUS_COLORS = { active: '#2e7d32', closed: '#9e9e9e' };

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $color }) => `${$color}15`};
  color: ${({ $color }) => $color};
`;

const Usage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 140px;
`;

const Track = styled.div`
  position: relative;
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #EEF1F5;
  overflow: hidden;
`;

const Fill = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  background: ${({ $color }) => $color};
  border-radius: 3px;
`;

const Pending = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $from }) => Math.min(100, $from)}%;
  width: ${({ $from, $pct }) => Math.max(0, Math.min(100 - Math.min(100, $from), $pct))}%;
  background: repeating-linear-gradient(135deg, #f3b74b 0 3px, #fde7bd 3px 6px);
`;

const UsageText = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  min-width: 38px;
  text-align: right;
`;

export const usageColor = (pct) => {
  if (pct >= 100) return '#E53935';
  if (pct >= 90) return '#eda100';
  return '#0291EF';
};

export const formatTry = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(Number(v) || 0);

// ISO tarihinin gün kısmı — yerel saat dilimi kaydırması olmadan (API "+03:00" döndürebilir).
export const dayPart = (v) => (v ? String(v).slice(0, 10) : '');
const showDate = (v, lang) => {
  const d = dayPart(v);
  if (!d) return '';
  const [y, m, g] = d.split('-').map(Number);
  return new Date(y, m - 1, g).toLocaleDateString(lang);
};

const bosMu = (v) => v === '' || v === undefined || v === null;

/// Gönderim öncesi: boş metin/sayı null'a (System.Text.Json "" okuyamaz → 400), tarihler
/// "yyyy-MM-dd", bütçe sayıya.
export const normalizeProjectPayload = (fd) => ({
  name: (fd.name || '').trim(),
  code: bosMu(fd.code) || !String(fd.code).trim() ? null : String(fd.code).trim(),
  description: bosMu(fd.description) || !String(fd.description).trim() ? null : String(fd.description).trim(),
  startDate: bosMu(fd.startDate) ? null : dayPart(fd.startDate),
  endDate: bosMu(fd.endDate) ? null : dayPart(fd.endDate),
  budget: bosMu(fd.budget) ? null : Number(fd.budget),
  status: fd.status || 'active',
});

/// Düzenleme çekmecesine giden değerler — tarihler gün kısmına indirilir.
export const toFormValues = (row) => (row ? {
  ...row,
  startDate: dayPart(row.startDate),
  endDate: dayPart(row.endDate),
  budget: row.budget ?? '',
  code: row.code ?? '',
  description: row.description ?? '',
} : null);

export const getProjectFormSchema = (t) => [
  { name: 'name', label: t('expenseProject.name'), type: 'text', grid: 8, required: true },
  { name: 'code', label: t('expenseProject.code'), type: 'text', grid: 4 },
  {
    name: 'status', label: t('expenseProject.status'), type: 'select', grid: 6, defaultValue: 'active',
    options: [
      { id: 'active', name: t('expenseProject.statusActive') },
      { id: 'closed', name: t('expenseProject.statusClosed') },
    ],
  },
  { name: 'budget', label: t('expenseProject.budget'), type: 'number', grid: 6, min: 0, step: 1 },
  { name: 'startDate', label: t('expenseProject.startDate'), type: 'date', grid: 6 },
  { name: 'endDate', label: t('expenseProject.endDate'), type: 'date', grid: 6 },
  { name: 'description', label: t('expenseProject.description'), type: 'textarea', grid: 12 },
  {
    name: 'closedHint', type: 'notice', grid: 12, tone: 'warning',
    content: t('expenseProject.closedHint'),
    hidden: (fd) => fd.status !== 'closed',
  },
];

export const useExpenseProjectApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getExpenseProjects(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await createExpenseProject(normalizeProjectPayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await updateExpenseProject(id, normalizeProjectPayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) await deleteExpenseProject(ids[0]);
      else await batchDeleteExpenseProjects(ids);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const statusLabels = useMemo(() => ({
    active: t('expenseProject.statusActive'),
    closed: t('expenseProject.statusClosed'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('expenseProject.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'code', label: t('expenseProject.code'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' }, customBodyRender: (v) => v || '—' },
    },
    {
      name: 'status', label: t('expenseProject.status'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['active', 'closed'],
          renderValue: (v) => statusLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => statusLabels[v] || v },
        customBodyRender: (v) => (
          <StatusBadge $color={PROJECT_STATUS_COLORS[v] || PROJECT_STATUS_COLORS.closed}>{statusLabels[v] || v}</StatusBadge>
        ),
      },
    },
    {
      name: 'startDate', label: t('expenseProject.period'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const bas = showDate(row?.startDate, lang);
          const bit = showDate(row?.endDate, lang);
          if (!bas && !bit) return '—';
          return `${bas || '…'} – ${bit || '…'}`;
        },
      },
    },
    {
      name: 'budget', label: t('expenseProject.budget'),
      options: { filter: false, sort: true, customBodyRender: (v) => (v == null ? '—' : formatTry(v)) },
    },
    {
      name: 'spentTotal', label: t('expenseProject.spent'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatTry(v) },
    },
    {
      name: 'budgetUsagePct', label: t('expenseProject.budgetUsage'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row || row.budget == null || !(row.budget > 0)) return '—';
          const pct = Number(v) || 0;
          const bekleyenPct = Math.round((Number(row.pendingTotal) || 0) * 100 / row.budget);
          const renk = usageColor(pct);
          return (
            <Usage
              data-testid={`usage-${row.id}`}
              title={t('expenseProject.usageTooltip', { spent: formatTry(row.spentTotal), pending: formatTry(row.pendingTotal), budget: formatTry(row.budget) })}
            >
              <Track>
                <Fill $pct={pct} $color={renk} />
                {bekleyenPct > 0 && <Pending $from={pct} $pct={bekleyenPct} />}
              </Track>
              <UsageText $color={renk}>{`%${pct}`}</UsageText>
            </Usage>
          );
        },
      },
    },
    { name: 'expenseCount', label: t('expenseProject.expenseCount'), options: { filter: false, sort: false } },
  ], [t, lang, data, statusLabels]);

  const objectSchema = useMemo(() => getProjectFormSchema(t), [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};
